import { useEffect, useState } from 'react'
import { Building2, Menu, ShoppingCart, DollarSign } from 'lucide-react'
import { useTranslation } from '../../core/hooks/useTranslation'
import { Card } from '../../components/ui/Card'
import { businessService } from '../../core/services/business.service'

interface DashboardStats {
  totalBusinesses: number
  todayOrders: number
  totalMenuItems: number
  totalRevenue: number
}

export default function BusinessDashboardPage() {
  const { t, isRTL } = useTranslation()
  const [stats, setStats] = useState<DashboardStats>({
    totalBusinesses: 0,
    todayOrders: 0,
    totalMenuItems: 0,
    totalRevenue: 0
  })
  const [isLoadingStats, setIsLoadingStats] = useState(true)

  useEffect(() => {
    fetchDashboardStats()
  }, [])

  const fetchDashboardStats = async () => {
    try {
      setIsLoadingStats(true)
      const dashboardStats = await businessService.getDashboardStats()
      setStats(dashboardStats)
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error)
    } finally {
      setIsLoadingStats(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }


  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('dashboard')}</h1>
        <p className="text-gray-600 mt-1">{t('welcomeMessage')}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Building2 className="h-8 w-8 text-blue-600" />
            </div>
            <div className={`ml-4 ${isRTL ? 'mr-4 ml-0' : ''}`}>
              <p className="text-sm font-medium text-gray-500">{t('totalBusinesses')}</p>
              <p className="text-2xl font-semibold text-gray-900">
                {isLoadingStats ? '...' : stats.totalBusinesses.toLocaleString()}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ShoppingCart className="h-8 w-8 text-purple-600" />
            </div>
            <div className={`ml-4 ${isRTL ? 'mr-4 ml-0' : ''}`}>
              <p className="text-sm font-medium text-gray-500">{t('todayOrders')}</p>
              <p className="text-2xl font-semibold text-gray-900">
                {isLoadingStats ? '...' : stats.todayOrders.toLocaleString()}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Menu className="h-8 w-8 text-green-600" />
            </div>
            <div className={`ml-4 ${isRTL ? 'mr-4 ml-0' : ''}`}>
              <p className="text-sm font-medium text-gray-500">{t('menuItems')}</p>
              <p className="text-2xl font-semibold text-gray-900">
                {isLoadingStats ? '...' : stats.totalMenuItems.toLocaleString()}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DollarSign className="h-8 w-8 text-yellow-600" />
            </div>
            <div className={`ml-4 ${isRTL ? 'mr-4 ml-0' : ''}`}>
              <p className="text-sm font-medium text-gray-500">{t('totalRevenue')}</p>
              <p className="text-2xl font-semibold text-gray-900">
                {isLoadingStats ? '...' : formatCurrency(stats.totalRevenue)}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}
